import {
  Input,
  Form,
  Select,
  Card,
  message,
  Table,
  Button,
  Modal,
  Tag,
  Switch,
} from 'antd';
// @ts-expect-error: No types available for react-copy-to-clipboard
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FormCard, FormContainer, FormSectionTitle, READ_ONLY_INPUT_STYLE } from '@leedpulse-monorepo/components';
import {
  countryCodes,
  cleanPhoneNumber,
  formatNumber,
  isValidPhoneNumber,
  removeLeadingZeros,
} from '@leedpulse-monorepo/utils';
import { UserRole, ContextStatus, EPhonePlatform, phonePlatformAsString, EChannel, channelAsString, ENumberProducts, numberProductsAsString } from '@leedpulse-monorepo/enums';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import SubscriptionsContext from '../../contexts/data/SubscriptionsContext';
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  PhoneOutlined,
  RightOutlined,
} from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import SMSContext from '../../contexts/data/SMSContext';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import { IAiReceptionistTriggers, INumber, INumberHistory } from '@leedpulse-monorepo/interfaces';
import updateNumber from '../../../domain/use_cases/numbers/updateNumber';
import deleteNumber from '../../../domain/use_cases/numbers/deleteNumber';
import getElevenLabsAgents, { ElevenLabsAgent } from '../../../domain/use_cases/numbers/getElevenLabsAgents';
import importNumberToElevenLabs from '../../../domain/use_cases/numbers/importNumberToElevenLabs';
import { NUMBERS_PAGE_PATH } from '../../pages/numbers/NumbersPage';
import { advancedOnRow } from '../../../core/utils/commonUtils';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';

const AI_RECEPTIONIST_TRIGGER_LABELS: { key: keyof IAiReceptionistTriggers; label: string }[] = [
  { key: 'do_not_disturb', label: 'Ne pas déranger' },
  { key: 'unreachable', label: 'Injoignable' },
  { key: 'outside_hours', label: 'Hors horaires' },
  { key: 'vacation', label: 'Vacances' },
  { key: 'no_answer', label: 'Pas de réponse' },
  { key: 'declined', label: 'Appel refusé' },
  { key: 'busy', label: 'Double appel' },
];

interface NumberDetailsProps {
  number: INumber;
}

const NumberDetails: React.FC<NumberDetailsProps> = ({ number }) => {
  const navigate = useNavigate();
  const subscriptionsContext = useContext(SubscriptionsContext);
  const smsContext = useContext(SMSContext);
  const authContext = useContext(AuthenticationContext);

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin].includes(
      authContext.authentication?.role
    );

  const [form] = Form.useForm<INumber>();
  const [contextStatus, setContextStatus] = useState(ContextStatus.IDLE);

  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [receiverTest, setReceiverTest] = useState('');
  const [messageTest, setMessageTest] = useState('');

  const [redirectionCountryCode, setRedirectionCountryCode] = useState<typeof countryCodes[0] | undefined>(countryCodes[0]);
  const [redirectionInput, setRedirectionInput] = useState('');

  const [elevenLabsAgents, setElevenLabsAgents] = useState<ElevenLabsAgent[]>([]);
  const [isImportingToElevenLabs, setIsImportingToElevenLabs] = useState(false);

  // Watchers to listen on form values changes
  const senderValue = Form.useWatch('sender', form);
  const providerValue = Form.useWatch('provider', form);
  const channelValue = Form.useWatch('channel', form);
  const redirectionValue = Form.useWatch('redirection', form);
  const elevenLabsPhoneNumberIdValue = Form.useWatch(['ai_receptionist', 'elevenlabs_phone_number_id'], form);
  const elevenLabsAgentIdValue = Form.useWatch(['ai_receptionist', 'elevenlabs_agent_id'], form);
  const aiReceptionistPublicLabelValue = Form.useWatch(['ai_receptionist', 'public_label'], form);
  const redirectionInitiated = useRef(false); // Prevent infinite loop

  const channelOptions = useMemo(() => {
    return providerValue === EPhonePlatform.twilio ? [
      {
        label: channelAsString(EChannel.whatsapp),
        value: EChannel.whatsapp,
      },
    ] : Object.values(EChannel).map((channel) => ({
      label: channelAsString(channel),
      value: channel,
    }));
  }, [providerValue]);

  const handleSendMessageClick = async () => {
    const values = form.getFieldsValue();
    if (values) {
      const payload = {
        receiver: receiverTest,
        sender: values.sender,
        message: messageTest,
        provider: values.provider,
        groupId: values.group_id,
        groupName: values.group_name,
      };
      smsContext.submitSMSTest(payload);
    }
  };

  const handleSubmit = async (values: INumber) => {
    if (number === null || authContext.authentication === null) return;
    setContextStatus(ContextStatus.LOADING);
    try {
      if (redirectionInput === '') values.redirection = ''; // Remove the country code if the number is wiped

      await updateNumber(values, authContext.authentication);
      setContextStatus(ContextStatus.SUCCESS);
      message.success('Mise à jour du numéro avec succès');
    } catch (e) {
      setContextStatus(ContextStatus.ERROR);
      message.error('Erreur lors de la mise à jour du numéro');
    }
  }

  const handleDelete = async () => {
    if (number === null || authContext.authentication === null) return;
    setContextStatus(ContextStatus.LOADING);
    try {
      await deleteNumber(number, authContext.authentication);
      setContextStatus(ContextStatus.SUCCESS);
      message.success('Numéro supprimé avec succès');
      navigate(NUMBERS_PAGE_PATH);
    } catch (e) {
      setContextStatus(ContextStatus.ERROR);
      message.error('Erreur lors de la suppression du numéro');
    }
  }

  const handleImportToElevenLabs = async () => {
    if (number === null || authContext.authentication === null) return;
    if (!elevenLabsAgentIdValue) {
      message.error('Veuillez sélectionner un agent ElevenLabs.');
      return;
    }
    setIsImportingToElevenLabs(true);
    try {
      const aiReceptionist = await importNumberToElevenLabs(
        {
          sender: number.sender,
          group_id: number.group_id,
          label: aiReceptionistPublicLabelValue || number.sender,
          agentId: elevenLabsAgentIdValue,
        },
        authContext.authentication
      );
      form.setFieldsValue({
        ai_receptionist: {
          ...form.getFieldValue('ai_receptionist'),
          elevenlabs_phone_number_id: aiReceptionist?.elevenlabs_phone_number_id,
          elevenlabs_agent_id: aiReceptionist?.elevenlabs_agent_id,
          elevenlabs_imported_at: aiReceptionist?.elevenlabs_imported_at,
        },
      });
      message.success('Numéro importé dans ElevenLabs avec succès');
    } catch (e) {
      message.error("Erreur lors de l'import du numéro dans ElevenLabs");
    } finally {
      setIsImportingToElevenLabs(false);
    }
  }

  useEffect(() => {
    if (senderValue) {
      subscriptionsContext.fetchBySender(
        senderValue
      );
    }
  }, [senderValue]);

  useEffect(() => {
    if (redirectionInitiated.current) return;
    const valueToInit = redirectionValue ?? form.getFieldValue('redirection');
    if (valueToInit == null || valueToInit === '') return;
    const digitsOnly = cleanPhoneNumber(valueToInit || '');

    const matchedCountry =
      countryCodes
        .slice()
        .sort((a, b) => b.code.length - a.code.length)
        .find((c) => digitsOnly.startsWith(c.code)) ?? countryCodes[0];

    const withoutCountryCode = digitsOnly.startsWith(matchedCountry.code)
      ? digitsOnly.substring(matchedCountry.code.length)
      : digitsOnly;

    const cleanedNumber = removeLeadingZeros(withoutCountryCode);

    // L'effet est couple a une ref (garde de pagination, generation de fetch) :
    // ecrire cette ref pendant le rendu serait une autre violation. On garde l'effet.
    // eslint-disable-next-line react/set-state-in-effect
    setRedirectionCountryCode(matchedCountry);
    setRedirectionInput(cleanedNumber);
    form.setFieldsValue({ redirection: matchedCountry.code + cleanedNumber });
    redirectionInitiated.current = true;
  }, [redirectionValue, form]);

  useEffect(() => {
    if (!redirectionCountryCode) return;
    const cleanedNumber = removeLeadingZeros(redirectionInput);
    form.setFieldsValue({ redirection: redirectionCountryCode.code + cleanedNumber });
  }, [redirectionCountryCode, redirectionInput, form]);

  useEffect(() => {
    if (smsContext.sentStatus === ContextStatus.SUCCESS) {
      message.success('SMS envoyé avec succès');
      // Synchro avec un systeme externe au rendu (DOM, storage, timer, toast,
      // navigation) : l'etat accompagne un effet de bord qui ne peut pas remonter au rendu.
      // eslint-disable-next-line react/set-state-in-effect
      setIsPhoneModalOpen(false);
    } else if (smsContext.sentStatus === ContextStatus.ERROR) {
      message.error('Erreur lors de l\'envoi du SMS');
    }
  }, [smsContext.sentStatus]);

  useEffect(() => {
    if (isReadOnly) return;
    getElevenLabsAgents(authContext.authentication)
      .then((agents) => {
        setElevenLabsAgents(agents);
        if (!form.getFieldValue(['ai_receptionist', 'elevenlabs_agent_id']) && agents.length > 0) {
          const defaultAgent = agents.find((agent) => agent.name === 'Donna') || agents[0];
          form.setFieldValue(['ai_receptionist', 'elevenlabs_agent_id'], defaultAgent.agentId);
        }
      })
      .catch(() => message.error('Erreur lors du chargement des agents ElevenLabs'));
  }, [authContext.authentication, isReadOnly]);


  return number === null ? (
    <Card loading />
  ) : (
    <FormCard
      title={senderValue || form.getFieldValue('sender')}
      collectionName={FirestoreCollections.numbers}
      onDeleted={isReadOnly ? undefined : handleDelete}
      onUpdated={isReadOnly ? undefined : () => form.submit()}
      isUpdateLoading={contextStatus === ContextStatus.LOADING}
      isDeleteLoading={contextStatus === ContextStatus.LOADING}
      headerActions={[
        <Button
          key="test-sms"
          type="default"
          icon={<PhoneOutlined />}
          style={{ marginRight: '1rem' }}
          onClick={() => setIsPhoneModalOpen(true)}
        >
          <span className="extra-label">Tester l'envoi</span>
        </Button>,
      ]}
    >
      <Form
        layout="vertical"
        form={form}
        requiredMark={false}
        initialValues={{ ...number }}
        onFinish={handleSubmit}
      >
        <Form.Item name="sender" hidden>
          <Input />
        </Form.Item>

        <FormSectionTitle title="Informations générales" />
        <FormContainer>
          <Form.Item
            label="ID"
            name="group_id"
            rules={[{ required: true, message: 'Veuillez entrer un ID' }]}
          >
            <Input
              disabled={contextStatus === ContextStatus.LOADING}
              readOnly={isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
            />
          </Form.Item>

          <Form.Item
            label="Nom"
            name="group_name"
          >
            <Input
              disabled={contextStatus === ContextStatus.LOADING}
              readOnly={isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
            />
          </Form.Item>

          <Form.Item
            label="Fournisseur"
            name="provider"
            rules={[{ required: true, message: 'Veuillez sélectionner un fournisseur' }]}
          >
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
            >
              {Object.keys(EPhonePlatform).map((key) => (
                <Select.Option
                  key={key}
                  value={EPhonePlatform[key as keyof typeof EPhonePlatform]}
                >
                  {phonePlatformAsString(
                    EPhonePlatform[key as keyof typeof EPhonePlatform]
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </FormContainer>

        <FormContainer>
          <Form.Item
            label="Canal"
            name="channel"
          >
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
              options={channelOptions}
            />
          </Form.Item>

          <Form.Item
            label="Pays"
            name="country"
          >
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
            >
              {countryCodes.map((country) => (
                <Select.Option key={country.name} value={country.name}>
                  {country.flag} {country.longName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Disponible"
            name="available"
          >
            <Select<boolean>
              disabled
            >
              <Select.Option key="true" value={true}>
                <CheckCircleFilled
                  style={{ color: 'green', marginRight: '8px' }}
                />
                Oui
              </Select.Option>
              <Select.Option key="false" value={false}>
                <CloseCircleFilled
                  style={{ color: 'red', marginRight: '8px' }}
                />
                Non
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Redirection"
          >
            <Form.Item name="redirection" hidden>
              <Input />
            </Form.Item>
            <Input
              disabled={contextStatus === ContextStatus.LOADING || channelValue === EChannel.redirecting}
              readOnly={isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              value={redirectionInput}
              onChange={(e) => {
                if (!redirectionCountryCode) return;

                const digitsOnly = cleanPhoneNumber(e.target.value);

                const withoutCountryCode = digitsOnly.startsWith(redirectionCountryCode.code)
                  ? digitsOnly.substring(redirectionCountryCode.code.length)
                  : digitsOnly;

                const cleanedNumber = removeLeadingZeros(withoutCountryCode);

                setRedirectionInput(cleanedNumber);
                form.setFieldsValue({ redirection: redirectionCountryCode.code + cleanedNumber });
              }}
              maxLength={redirectionCountryCode?.digitsLength}
              allowClear
              placeholder={
                redirectionCountryCode
                  ? `Numéro (${redirectionCountryCode.digitsLength} chiffres)`
                  : undefined
              }
              addonBefore={
                <Select
                  value={redirectionCountryCode?.name}
                  style={{ width: 100 }}
                  onChange={(value) => {
                    const country = countryCodes.find((c) => c.name === value);
                    if (country) {
                      setRedirectionCountryCode(country);
                    }
                  }}
                  popupMatchSelectWidth={false}
                  disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
                  showSearch
                  filterOption={(input, option) => {
                    const childText = option?.children?.toString() || '';
                    return (
                      childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                >
                  {countryCodes.map((country) => (
                    <Select.Option key={country.name} value={country.name}>
                      {country.flag}{' '}
                      {country.shortName || country.name.substring(0, 3)}
                    </Select.Option>
                  ))}
                </Select>
              }
              suffix={
                form.getFieldValue('redirection') &&
                  isValidPhoneNumber(
                    form.getFieldValue('redirection'),
                    redirectionCountryCode?.digitsLength || 0
                  ) ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : undefined
              }
              showCount
            />
          </Form.Item>

          <Form.Item
            label="Produit"
            name="product"
          >
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
            >
              {Object.keys(ENumberProducts).map((key) => (
                <Select.Option
                  key={key}
                  value={ENumberProducts[key as keyof typeof ENumberProducts]}
                >
                  {numberProductsAsString(
                    ENumberProducts[key as keyof typeof ENumberProducts]
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </FormContainer>

        <FormContainer oneColumnMax>
          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea
              disabled={contextStatus === ContextStatus.LOADING}
              readOnly={isReadOnly}
              rows={6}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
            />
          </Form.Item>
        </FormContainer>

        <FormSectionTitle title="IA Réceptionniste" />
        <FormContainer>
          <Form.Item name={['ai_receptionist', 'elevenlabs_phone_number_id']} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={['ai_receptionist', 'elevenlabs_imported_at']} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={['ai_receptionist', 'configured_at']} hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label="Activé"
            name={['ai_receptionist', 'enabled']}
            valuePropName="checked"
          >
            <Switch
              disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
              onChange={(checked) => {
                if (!checked || elevenLabsPhoneNumberIdValue || isImportingToElevenLabs) return;
                handleImportToElevenLabs();
              }}
            />
          </Form.Item>

          <Form.Item
            label="Nom public"
            name={['ai_receptionist', 'public_label']}
            tooltip="Nom ou identité annoncé par l'IA aux appelants. Ex : agent indépendant → « Martin », équipe → « l'équipe Martin », agence → « l'agence Horizon »."
          >
            <Input
              disabled={contextStatus === ContextStatus.LOADING}
              readOnly={isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
            />
          </Form.Item>

          <Form.Item
            label="Personne / service de rappel"
            name={['ai_receptionist', 'callback_label']}
            tooltip="Personne ou service annoncé pour le rappel. Ex : agent indépendant → « Martin », équipe/agence → « l'équipe »."
          >
            <Input
              disabled={contextStatus === ContextStatus.LOADING}
              readOnly={isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
            />
          </Form.Item>

          <Form.Item
            label="Agent ElevenLabs"
            name={['ai_receptionist', 'elevenlabs_agent_id']}
            tooltip="Agent conversationnel ElevenLabs à associer au numéro lors de l'import."
          >
            <Select
              disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              placeholder="Sélectionner un agent"
              options={elevenLabsAgents.map((agent) => ({ label: agent.name, value: agent.agentId }))}
            />
          </Form.Item>

          <Form.Item
            label="Décroche quand"
            tooltip="Réglé par l'utilisateur dans l'app. Sans réglage, l'IA prend tous les cas."
          >
            {AI_RECEPTIONIST_TRIGGER_LABELS.map(({ key, label }) => (
              <Tag key={key} color={number.ai_receptionist?.triggers?.[key] === false ? undefined : 'green'}>
                {number.ai_receptionist?.triggers?.[key] === false ? '✗' : '✓'} {label}
              </Tag>
            ))}
          </Form.Item>

          <Form.Item label="Import du numéro dans ElevenLabs">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {isImportingToElevenLabs ? (
                <Tag color="blue">⏳ Import en cours...</Tag>
              ) : elevenLabsPhoneNumberIdValue ? (
                <Tag color="green" title={elevenLabsPhoneNumberIdValue}>✅ Numéro importé</Tag>
              ) : (
                <Tag>Numéro non importé</Tag>
              )}
              {!isReadOnly && (
                <Button
                  loading={isImportingToElevenLabs}
                  disabled={contextStatus === ContextStatus.LOADING}
                  onClick={handleImportToElevenLabs}
                >
                  {elevenLabsPhoneNumberIdValue ? 'Réimporter le numéro dans ElevenLabs' : 'Importer le numéro dans ElevenLabs'}
                </Button>
              )}
            </div>
          </Form.Item>
        </FormContainer>

        <FormSectionTitle
          title={`Abonnements (${subscriptionsContext.count})`}
        />
        <Table
          pagination={false}
          showHeader={false}
          columns={[
            {
              title: 'Nom',
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: 'Action',
              render: () => <a>Voir le détail</a>,
            },
          ]}
          rowKey={(record) => record.id}
          dataSource={subscriptionsContext.items}
          onRow={(record) =>
            advancedOnRow({
              url: `${SUBSCRIPTIONS_PAGE_PATH}/${record.id}`,
              navigate,
              className: 'table-row',
            })
          }
        />
      </Form>

      <div style={{ marginTop: '2rem' }}>
        <FormSectionTitle title={`Historiques (${number.history?.length || 0})`} />

        <Table
          dataSource={number.history}
          pagination={false}
          expandable={{
            rowExpandable: (record: INumberHistory) =>
              record.id_subscriptions.length > 0,
            expandedRowRender: (record: INumberHistory) => {
              return (
                <Table
                  showHeader={false}
                  pagination={false}
                  columns={[
                    {
                      title: 'Abonnement',
                      key: 'id_subscriptions_sub',
                      render: (text: string) => {
                        const subscription = subscriptionsContext.items.find(
                          (subscription) => subscription.id === text
                        );

                        return <span>{subscription ? subscription.name : text}</span>;
                      },
                    },
                    {
                      title: 'Id',
                      key: 'id',
                      render: (text: string) => {
                        return (
                          <div
                            style={{ display: 'flex', justifyContent: 'start' }}
                          >
                            <CopyToClipboard
                              text={text}
                              onCopy={() => message.success('ID Copié')}
                            >
                              <div
                                className="detail-form-card__title"
                                style={{ cursor: 'pointer' }}
                              >
                                <Tag
                                  bordered={true}
                                  style={{
                                    fontSize: '1em',
                                    marginTop: '0.5em',
                                    marginBottom: '0.5em',
                                  }}
                                >
                                  {text}
                                </Tag>
                              </div>
                            </CopyToClipboard>
                          </div>
                        );
                      },
                    },
                    {
                      title: 'Action',
                      key: 'actions',
                      render: (text: string) => (
                        <div style={{ display: 'flex', justifyContent: 'end' }}>
                          <Button type="primary" icon={<RightOutlined />} onClick={() => navigate(`${SUBSCRIPTIONS_PAGE_PATH}/${text}`)}>
                            Voir l'abonnement
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                  rowKey={(record: string) => record}
                  dataSource={record.id_subscriptions}
                />
              );
            },
          }}
          columns={[
            {
              title: 'Date',
              dataIndex: 'timestamp',
              key: 'timestamp',
              render: (text: string) => {
                return <span>{new Date(text).toLocaleString()}</span>;
              },
            },
            {
              title: 'SMS envoyés (Total)',
              dataIndex: 'sms',
              key: 'sms',
              render: (text: number) => {
                return <span>{formatNumber(text)}</span>;
              },
            },
            {
              title: 'Appels (Total)',
              dataIndex: 'calls',
              key: 'calls',
              render: (text: number) => {
                return <span>{formatNumber(text)}</span>;
              },
            },
            {
              title: 'Messages vocaux (Total)',
              dataIndex: 'voicemails',
              key: 'voicemails',
              render: (text: number) => {
                return <span>{formatNumber(text)}</span>;
              },
            },

            {
              title: 'Abonnements',
              dataIndex: 'id_subscriptions',
              key: 'id_subscriptions',
              render: (text: string[]) => {
                return <span>{text.length}</span>;
              },
            },
            Table.EXPAND_COLUMN,
          ]}
          rowKey={(record: INumberHistory) => record.timestamp}
        />
      </div>

      <Modal
        title="Tester l'envoi"
        open={isPhoneModalOpen}
        footer={() => (
          <Button
            type="primary"
            disabled={contextStatus === ContextStatus.LOADING || isReadOnly}
            loading={smsContext.sentStatus === ContextStatus.LOADING}
            onClick={handleSendMessageClick}
          >
            Envoyer
          </Button>
        )}
        onCancel={() => setIsPhoneModalOpen(false)}
      >
        <Form layout="vertical" style={{ marginTop: '1rem' }}>
          <FormContainer oneColumnMax>
            <Form.Item label="Numéro du destinataire">
              <Input
                disabled={contextStatus === ContextStatus.LOADING}
                readOnly={isReadOnly}
                placeholder="Ex: 33612345678 (format international)"
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) => {
                  // Allowed only numeric
                  e.target.value = e.target.value.replace(/\D/g, '');
                  setReceiverTest(e.target.value);
                }}
                value={receiverTest}
              />
            </Form.Item>
            <Form.Item label="Message">
              <TextArea
                placeholder="Votre message ici..."
                rows={4}
                disabled={contextStatus === ContextStatus.LOADING}
                readOnly={isReadOnly}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) => setMessageTest(e.target.value)}
              />
            </Form.Item>
          </FormContainer>
        </Form>
      </Modal>
    </FormCard>
  );
};

export default NumberDetails;
